import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useBrand } from "../../context/BrandContext";
import { fetchBookingConfirmation } from "../../services/rentalService";
import "../../styles/booking.css";

export default function BookConfirmation() {
  const { clubCode: paramClubCode, bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get("embed") === "1";
  const accepted = searchParams.get("accepted") === "1";
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const clubCode = paramClubCode || brand?.club_subdomain || brand?.club_id;
  const lang = i18n.language?.slice(0, 2) || "en";

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!clubCode || !bookingId) return;
    fetchBookingConfirmation(clubCode, bookingId, lang)
      .then((res) => setBooking(res.data))
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [clubCode, bookingId, lang]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="booking-confirm-box">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const waitingOpponent = booking?.status === "open_challenge";
  const confirmed = booking?.status === "confirmed";

  return (
    <div className={`booking-page${embed ? " embed-mode" : ""}`}>
      <Box className="booking-confirm-box">
        {confirmed && (
          <>
            <Typography variant="h5" gutterBottom>
              {accepted ? t("rental_challenge_matched") : t("rental_confirmed_title")}
            </Typography>
            <Typography color="text.secondary" paragraph>
              {new Date(booking.starts_at).toLocaleString(lang)}
            </Typography>
            {booking.access_code && (
              <>
                <Typography>{t("rental_access_code_label")}</Typography>
                <div className="access-code">{booking.access_code}</div>
              </>
            )}
          </>
        )}

        {waitingOpponent && (
          <>
            <Typography variant="h5" gutterBottom>
              {t("rental_waiting_opponent")}
            </Typography>
            <Typography color="text.secondary" paragraph>
              {t("rental_share_challenge")}
            </Typography>
            {booking.share_url && (
              <Typography
                component="a"
                href={booking.share_url}
                sx={{ wordBreak: "break-all", display: "block", mb: 2 }}
              >
                {booking.share_url}
              </Typography>
            )}
          </>
        )}

        <Button component={Link} to={`/book/${clubCode}${embed ? "?embed=1" : ""}`} variant="outlined">
          {t("rental_back_to_booking")}
        </Button>
      </Box>
    </div>
  );
}
